import { supabase } from '../supabase/config';
import { logActivity } from '../utils/activityLogger';

// Convert snake_case DB row to camelCase for app consumption
const toUser = (row) => {
    if (!row) return null;
    return {
        id: row.id,
        email: row.email,
        firstName: row.first_name,
        lastName: row.last_name,
        position: row.position,
        phone: row.phone,
        role: row.role,
        tenantId: row.tenant_id,
        isSuperAdmin: row.is_super_admin,
        createdAt: row.created_at,
    };
};

const toRole = (row) => {
    if (!row) return null;
    return {
        id: row.id,
        tenantId: row.tenant_id,
        name: row.name,
        permissions: row.permissions,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
};

export const roleService = {
    subscribeToUsers(tenantId, isSuperAdmin, callback) {
        // Initial fetch
        const fetchData = async () => {
            let query = supabase.from('users').select('*');
            if (!tenantId && isSuperAdmin) {
                query = query.limit(1000);
            } else {
                query = query.eq('tenant_id', tenantId);
            }
            const { data, error } = await query;
            if (error) {
                console.error("Users fetch error:", error);
                callback([]);
                return;
            }
            callback((data || []).map(toUser));
        };

        fetchData();

        // Realtime subscription
        const channelConfig = {
            event: '*',
            schema: 'public',
            table: 'users',
        };
        if (tenantId) {
            channelConfig.filter = `tenant_id=eq.${tenantId}`;
        }

        const channel = supabase
            .channel(`users-${tenantId || 'all'}`)
            .on('postgres_changes', channelConfig, () => {
                fetchData();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    },

    subscribeToRoles(tenantId, callback) {
        if (!tenantId) return () => {};

        // Initial fetch
        const fetchData = async () => {
            const { data, error } = await supabase
                .from('roles')
                .select('*')
                .eq('tenant_id', tenantId);
            if (error) {
                console.error("Roles fetch error:", error);
                callback([]);
                return;
            }
            callback((data || []).map(toRole));
        };

        fetchData();

        // Realtime subscription
        const channel = supabase
            .channel(`roles-${tenantId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'roles',
                filter: `tenant_id=eq.${tenantId}`,
            }, () => {
                fetchData();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    },

    async saveRole(tenantId, roleData, roleId, userId, userEmail) {
        const now = new Date().toISOString();
        const data = {
            tenant_id: tenantId,
            name: roleData.name,
            permissions: roleData.permissions,
            updated_at: now,
        };

        let res;
        if (roleId) {
            const { data: updated, error } = await supabase
                .from('roles')
                .update(data)
                .eq('id', roleId)
                .select()
                .single();
            if (error) throw error;
            res = updated;
        } else {
            const { data: inserted, error } = await supabase
                .from('roles')
                .insert({ ...data, created_at: now })
                .select()
                .single();
            if (error) throw error;
            res = inserted;
        }

        await logActivity(tenantId, userEmail, userId, roleId ? 'UPDATE_ROLE' : 'CREATE_ROLE', `${roleId ? 'Updated' : 'Created'} role: ${roleData.name}`);
        return res;
    },

    async deleteRole(tenantId, roleId, roleName, userId, userEmail) {
        const { error } = await supabase
            .from('roles')
            .delete()
            .eq('id', roleId);
        if (error) throw error;

        await logActivity(tenantId, userEmail, userId, 'DELETE_ROLE', `Deleted role: ${roleName}`);
    },

    async createInvite(tenantId, userData, userId, userEmail) {
        const token = Math.random().toString(36).substring(2, 15);
        const now = new Date().toISOString();

        const { error } = await supabase
            .from('invitations')
            .insert({
                tenant_id: tenantId,
                email: userData.email,
                first_name: userData.firstName,
                last_name: userData.lastName,
                role: userData.role,
                token,
                status: 'pending',
                created_at: now,
            });
        if (error) throw error;

        const link = `${window.location.origin}/accept-invite?token=${token}`;
        await logActivity(tenantId, userEmail, userId, 'INVITE_USER', `Invited user: ${userData.email}`);
        return link;
    },

    async seedDefaultRoles(tenantId, userId, userEmail) {
        const now = new Date().toISOString();
        const defaultRoles = [
            {
                name: 'Ortak',
                permissions: {
                    customers: { view: true, create: true, edit: true, delete: true },
                    menu: { view: true, create: true, edit: true, delete: true },
                    orders: { view: true, create: true, edit: true, delete: true },
                    inventory: { view: true, edit: true },
                    accounting: { view: true },
                    analytics: { view: true },
                    notifications: { view: true },
                    support: { view: true, create: true },
                    activities: { view: true },
                    settings: { view: true, edit: true },
                    roles: { view: true, manage: true }
                }
            },
            {
                name: 'Müdür',
                permissions: {
                    customers: { view: true, create: true, edit: true, delete: true },
                    menu: { view: true, create: true, edit: true, delete: true },
                    orders: { view: true, create: true, edit: true, delete: true },
                    inventory: { view: true, edit: true },
                    accounting: { view: true },
                    analytics: { view: true },
                    notifications: { view: true },
                    support: { view: true, create: true },
                    activities: { view: true },
                    settings: { view: true, edit: false },
                    roles: { view: true, manage: true }
                }
            },
            {
                name: 'Kasiyer',
                permissions: {
                    customers: { view: true, create: true, edit: true, delete: false },
                    menu: { view: true, create: false, edit: false, delete: false },
                    orders: { view: true, create: true, edit: true, delete: true },
                    accounting: { view: true },
                    rewards: { view: true, create: true },
                    discounts: { view: true, create: true },
                    notifications: { view: true },
                    activities: { view: true },
                    settings: { view: false, edit: false },
                    roles: { view: false, manage: false }
                }
            },
            {
                name: 'Garson',
                permissions: {
                    customers: { view: true, create: true, edit: false, delete: false },
                    menu: { view: true, create: false, edit: false, delete: false },
                    orders: { view: true, create: true, edit: true, delete: false },
                    notifications: { view: true },
                    rewards: { view: true },
                    activities: { view: false },
                    settings: { view: false, edit: false },
                    roles: { view: false, manage: false }
                }
            },
            {
                name: 'Mutfak',
                permissions: {
                    menu: { view: true, create: false, edit: false, delete: false },
                    orders: { view: true, create: false, edit: true, delete: false },
                    inventory: { view: true, edit: true },
                    notifications: { view: true },
                    activities: { view: false },
                    settings: { view: false, edit: false },
                    roles: { view: false, manage: false }
                }
            }
        ];

        // Get existing roles to avoid duplicates
        const { data: existingRoles } = await supabase
            .from('roles')
            .select('name')
            .eq('tenant_id', tenantId);
        
        const existingNames = (existingRoles || []).map(r => r.name);
        const rolesToInsert = defaultRoles.filter(r => !existingNames.includes(r.name));

        if (rolesToInsert.length === 0) return;

        const inserts = rolesToInsert.map(role => ({
            tenant_id: tenantId,
            name: role.name,
            permissions: role.permissions,
            created_at: now,
            updated_at: now
        }));

        const { error } = await supabase.from('roles').insert(inserts);
        if (error) throw error;

        await logActivity(tenantId, userEmail, userId, 'SEED_ROLES', `Created default roles: ${rolesToInsert.map(r => r.name).join(', ')}`);
    }
};
